import React, { useEffect, useState } from 'react';
import withStyles from '@mui/styles/withStyles';
import styles from './EmbeddingConfiguration.styles';
import Content from 'studio/components/Content/Content';
import BuildIcon from '@mui/icons-material/BuildRounded';
import TextField from 'studio/components/TextField';
import Link from 'studio/components/Link';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import services from 'services';
import { Controller, useForm } from 'react-hook-form/dist/index.ie11';
import { yupResolver } from '@hookform/resolvers/dist/ie11/yup';
import * as yup from 'yup';
import { useLoader } from 'components/loaders/FadeLoader';
import { useMessage } from 'studio/components/Message';
import { trackEvent } from 'utils/eventTracking';
import FormControlLabel from '@mui/material/FormControlLabel';
import PageTitle from 'studio/components/PageTitle';
import HelpButton from 'studio/components/HelpButton';

const EmbeddingConfiguration = ({ classes }) => {
  useEffect(() => {
    trackEvent('Admin Embedded Configuration Opened');
  }, []);

  const { withLoader, loading } = useLoader();
  const { successMessage, errorMessage } = useMessage();

  // the embedding configuration
  const [embeddingConfiguration, setEmbeddingConfiguration] = useState(undefined);

  // retrieves the existing embedding configuration
  const getEmbeddingConfiguration = async () => {
    const response = await services.getEmbeddingConfiguration();
    const embeddingConfiguration = response.data || null;
    setEmbeddingConfiguration(embeddingConfiguration);
  };

  // replaces the embedding configuration
  const replaceEmbeddingConfiguration = async embeddingConfiguration => {
    const response = await withLoader(services.replaceEmbeddingConfiguration(embeddingConfiguration));
    if (response.success) {
      setEmbeddingConfiguration(response.data);
      successMessage('The embedding configuration has successfully been updated');
    } else {
      const message =
        response?.response?.data?.data?.message || 'There was an error updating the embedding configuration';
      errorMessage(message);
    }
  };

  // initially retrieves the embedding configuration
  useEffect(() => {
    getEmbeddingConfiguration();
  }, []);

  // the validation rules for the form fields
  const schema = yup.object().shape({
    isEnabled: yup.bool(),
    cspFrameAncestors: yup.string().required()
  });

  const form = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      isEnabled: false,
      cspFrameAncestors: null
    }
  });
  const { handleSubmit, control, errors, reset, formState } = form;

  // resets the form whenever the embedding configuration changes
  useEffect(() => {
    reset(embeddingConfiguration?.data);
  }, [embeddingConfiguration]);

  return (
    <Content loading={embeddingConfiguration === undefined} hasUnsavedChanges={loading || formState.isDirty}>
      <div className={classes.root}>
        <PageTitle icon={BuildIcon}>Embedding Configuration</PageTitle>

        <form autoComplete="off" className={classes.form}>
          <Controller
            control={control}
            name="isEnabled"
            render={({ ref, value }) => (
              <FormControlLabel
                control={
                  <Switch
                    inputRef={ref}
                    onChange={event => {
                      const isEnabled = event.target.checked;
                      handleSubmit(newJwtAuthenticationConfiguration =>
                        replaceEmbeddingConfiguration({ ...newJwtAuthenticationConfiguration, isEnabled })
                      )();
                    }}
                    checked={value}
                  />
                }
                label={'Embedding is ' + (value ? 'enabled' : 'disabled')}
              />
            )}
          />

          <Typography variant="h2" gutterBottom>
            Allowed Origins
            <HelpButton
              docsUrl="/embedded/embedded-guide/#2-configure-allowed-hosts"
              small
              style={{ marginLeft: 10 }}
            />
          </Typography>
          <Typography variant="body1" gutterBottom>
            Configure the origins (URLs) of your web applications where you want to allow Veezoo to be embedded into an
            iframe. You can specify multiple origins separated by spaces.
          </Typography>
          <Controller
            as={TextField}
            control={control}
            name="cspFrameAncestors"
            label="Allowed origins"
            description={
              <>
                Enter the origins in the syntax defined by the CSP{' '}
                <Link
                  href="https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Content-Security-Policy/frame-ancestors"
                  newWindow
                >
                  frame-ancestors
                </Link>{' '}
                directive
              </>
            }
            placeholder="https://*.mycompany.com"
            error={!!errors.cspFrameAncestors}
          />

          <div className={classes.actions}>
            <Button
              color="primary"
              variant="contained"
              onClick={handleSubmit(replaceEmbeddingConfiguration)}
              disabled={loading || !formState.isDirty}
            >
              Save
            </Button>
          </div>
        </form>
      </div>
    </Content>
  );
};

export default withStyles(styles)(EmbeddingConfiguration);
